import React, { useState } from "react";
import moment from "moment";
import { mdiChevronRight, mdiMinus, mdiPlus } from "@mdi/js";
import MdiIcon from "../../../components/device/app/MdiIcon";
import { WEEK } from "../../../automation/format";
import { TypeIcon } from "../RunViews";

// Building blocks of the create flow (app/screens/Automation/create/controls.js),
// in the dashboard's hx- look.

export const Card = ({ children, className = "" }) => <section className={`hx-auto-step-card ${className}`}>{children}</section>;

export const Label = ({ children, id }) => (
  <h3 id={id} className="hx-auto-step-label">
    {children}
  </h3>
);

export const Hint = ({ children, className = "" }) => <p className={`hx-auto-step-hint ${className}`}>{children}</p>;

// The step's question, e.g. "What starts it?"
export const StepTitle = ({ step, of, name, title, hint }) => (
  <div className="hx-auto-steptitle">
    <p>{`Step ${step} of ${of} · ${name}`}</p>
    {title ? <h2>{title}</h2> : null}
    {hint ? <Hint>{hint}</Hint> : null}
  </div>
);

const DAY_NAMES = { 0: "Sun", 1: "Mon", 2: "Tue", 3: "Wed", 4: "Thu", 5: "Fri", 6: "Sat" };

// Day circles, Monday first; days are 0 = Sunday ... 6 = Saturday
export const DayCircles = ({ days, onChange, label = "Days" }) => {
  const toggle = (day) => onChange(days.includes(day) ? days.filter((d) => d !== day) : [...days, day].sort());
  return (
    <div className="hx-auto-days" role="group" aria-label={label}>
      {WEEK.map((day) => (
        <button
          key={day}
          type="button"
          className={`hx-auto-day-circle ${days.includes(day) ? "is-on" : ""}`}
          aria-pressed={days.includes(day)}
          onClick={() => toggle(day)}
        >
          {DAY_NAMES[day]}
        </button>
      ))}
    </div>
  );
};

// A browser without a time picker shows a text box: these are read from it too.
const TYPED_TIMES = ["HH:mm", "H:mm", "h:mm A", "h:mmA", "h:mm a", "h:mma"];

// A time of day, "HH:mm", in the browser's time picker. What's being typed
// shows until the box loses focus; each complete time is passed on.
export const TimeBox = ({ value, onChange, label }) => {
  const [typing, setTyping] = useState(null);
  const change = (event) => {
    setTyping(event.target.value);
    const time = moment(event.target.value.trim(), TYPED_TIMES, true);
    if (time.isValid()) onChange(time.format("HH:mm"));
  };
  return (
    <input
      type="time"
      className="hx-input hx-auto-time"
      value={typing ?? value ?? ""}
      onChange={change}
      onBlur={() => setTyping(null)}
      aria-label={label}
    />
  );
};

// Two or three choices side by side, like the Motor 1 / Motor 2 switch
export const Segmented = ({ options, value, onChange, label, compact = false }) => (
  <div className={`hx-auto-seg ${compact ? "is-compact" : ""}`} role="radiogroup" aria-label={label}>
    {options.map((option) => (
      <button
        key={String(option.key)}
        type="button"
        role="radio"
        aria-checked={option.key === value}
        className={option.key === value ? "is-on" : ""}
        onClick={() => onChange(option.key)}
      >
        {option.label}
      </button>
    ))}
  </div>
);

// − value + for whole numbers
export const Stepper = ({ value, min, max, step = 1, onChange, label, format = (v) => String(v) }) => {
  const set = (next) => onChange(Math.max(min, Math.min(max, next)));
  return (
    <div className="hx-auto-stepper" role="group" aria-label={label}>
      <button type="button" onClick={() => set(value - step)} aria-label={`Decrease ${label.toLowerCase()}`}>
        <MdiIcon path={mdiMinus} size={20} />
      </button>
      <span>{format(value)}</span>
      <button type="button" onClick={() => set(value + step)} aria-label={`Increase ${label.toLowerCase()}`}>
        <MdiIcon path={mdiPlus} size={20} />
      </button>
    </div>
  );
};

// Quick picks, e.g. 15 min · 30 min · 1 h, as chips
export const Chips = ({ options, value, onChange, label }) => (
  <div className="hx-chips hx-auto-picks" role="group" aria-label={label}>
    {options.map((option) => (
      <button
        key={String(option.key)}
        type="button"
        className={`hx-chip ${option.key === value ? "is-active" : ""}`}
        aria-pressed={option.key === value}
        onClick={() => onChange(option.key)}
      >
        {option.label}
      </button>
    ))}
  </div>
);

// A row in "What starts it?" and in a device's Add sheet: the type's icon,
// title, an example, and a chevron
export const OptionRow = ({ type, title, text, badge, disabled, onPress }) => (
  <button type="button" className="hx-auto-option" disabled={disabled} onClick={onPress}>
    <TypeIcon type={type} size={40} />
    <span className="hx-auto-option-text">
      <span className="hx-auto-option-title">
        {title}
        {badge ? <span className="hx-auto-pill">{badge}</span> : null}
      </span>
      {text ? <span className="hx-auto-option-sub">{text}</span> : null}
    </span>
    {disabled ? null : <MdiIcon path={mdiChevronRight} size={22} />}
  </button>
);
